import React, { useState, useEffect } from "react";
import {
  Home,
  FileText,
  Brain,
  Video,
  Settings,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../routers/axios";
import { authAPI } from "../routers/api";
import DashboardOverview from "../components/DashboardOverview";
import { QuizCreationModal } from "../components/new_test";
import TestList from "../components/TestList";
import QuestionList from "../pages/questions/QuestionList";
import AddQuestionComponent from "../components/add_question";
import ScenarioDetailModal from "../components/Modals/ScenarioDetailModal";
import CreateScenarioModal from "../components/Modals/CreateScenarioModal";
import ResultsSection from "../components/ResultsSection";
import VideoAI from "../components/VideoAI";
import SettingsPage from "../components/SettingsPage";

const TeacherCMS = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeModule = location.pathname.split("/")[3] || "dashboard";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tests, setTests] = useState([]);
  const [attemptCount, setAttemptCount] = useState(0);
  const [scenarioModalOpen, setScenarioModalOpen] = useState(false);
  const [testForScenario, setTestForScenario] = useState(null);
  const [scenarioDetailOpen, setScenarioDetailOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [refreshScenarios, setRefreshScenarios] = useState(null);

  const fetchTests = async () => {
    try {
      const res = await axios.get("/test/list");
      setTests(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAttemptCount = async () => {
    try {
      const res = await axios.get(
        "/test_attempts/review/all-tests?page=1&page_size=1"
      );
      const total = res.data?.total_records || 0;
      console.log("🧠 Tổng số bài làm:", total);
      setAttemptCount(total);
    } catch (err) {
      console.error("Lỗi khi lấy tổng số bài làm:", err);
    }
  };

  useEffect(() => {
    fetchTests();
    fetchAttemptCount();
  }, []);

  const handleSidebarClick = (id) => navigate(`/teacher/cms/${id}`);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const sidebarItems = [
    { id: "dashboard", icon: Home, label: "Dashboard", badge: null },
    { id: "test", icon: FileText, label: "Test", badge: null },
    { id: "questions", icon: FileText, label: "Questions", badge: null },
    { id: "results", icon: Brain, label: "Kết quả bài làm", badge: null },
    { id: "videos", icon: Video, label: "Video AI", badge: null },
    { id: "settings", icon: Settings, label: "Cài đặt", badge: null },
  ];

  const renderTests = () => (
    <TestList
      tests={tests}
      onCreate={() => setShowCreateModal(true)}
      onOpenScenarioModal={(test, refreshScenarios) => {
        setTestForScenario(test);
        setScenarioModalOpen(true);
        setRefreshScenarios(() => refreshScenarios);
      }}
      onSelectScenario={(scenario) => {
        setSelectedScenario(scenario);
        setScenarioDetailOpen(true);
      }}
    />
  );

  const renderContent = () => {
    switch (activeModule) {
      case "dashboard":
        return <DashboardOverview />;
      case "test":
        return renderTests();
      case "questions":
        return <QuestionList />;
      case "results":
        return <ResultsSection />;
      case "videos":
        return <VideoAI />;
      case "settings":
        return <SettingsPage />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex-shrink-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col w-64 h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <div className="text-xl font-bold text-blue-600">
              Smart<span className="text-gray-800">English</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSidebarClick(item.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  activeModule === item.id
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-bold leading-4 text-white bg-red-500 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Đăng xuất
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4 sm:px-2">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-400 hover:text-gray-600 mr-2"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                {sidebarItems.find((item) => item.id === activeModule)?.label}
              </h1>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {renderContent()}
        </main>
      </div>

      {/* Quiz Modal */}
      <QuizCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(newTest) => setTests((prev) => [newTest, ...prev])}
      />

      {/* Scenario Modal */}
      <CreateScenarioModal
        test={testForScenario}
        isOpen={scenarioModalOpen}
        onClose={() => setScenarioModalOpen(false)}
        onCreated={() => {
          if (refreshScenarios) refreshScenarios();
        }}
      />

      {/* Scenario Detail Modal */}
      {scenarioDetailOpen && selectedScenario && (
        <ScenarioDetailModal
          scenario={selectedScenario}
          onClose={() => {
            setScenarioDetailOpen(false);
            setSelectedScenario(null);
          }}
        />
      )}
    </div>
  );
};

export default TeacherCMS;